import {
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Evaluation } from "../../common/Evaluation";
import { Fleet } from "../../common/Fleet";
import { JobRatingLabel } from "../../common/JobOrder";
import { useGetEvaluationArchivesQuery } from "../../redux/api/evaluation";
import { useGetFleetsQuery } from "../../redux/api/fleet";
import useDocumentTitle from "../../util/useDocumentTitle";
import ArrowRightAltOutlinedIcon from "@mui/icons-material/ArrowRightAltOutlined";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectCurrentCaptain, selectCurrentDispatch } from "../../redux/authentication/authSlice";
import { Dispatch } from "../../common/Dispatch";

const EvaluationArchive = () => {
  useDocumentTitle("Archived Evaluations");
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [startDate, setStartDate] = useState<string | undefined>(
    new Date(new Date().getFullYear(), 0, 1).toISOString()
  );
  const [endDate, setEndDate] = useState<string | undefined>(
    new Date(new Date().getFullYear(), 11, 31).toISOString()
  );
  const isCaptain = useSelector(selectCurrentCaptain);
  const currentDispatch: Dispatch = useSelector(selectCurrentDispatch);

  const { data: fleets, isLoading: isFleetsLoading } = useGetFleetsQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
      skip: isCaptain !== false,
    }
  );
  const { data: evaluationArchives, isLoading: isEvaluationArchivesLoading } =
    useGetEvaluationArchivesQuery(
      {
        fleetId: !isCaptain && fleet != null && fleet.id != null ? fleet.id : undefined,
        vesselId: isCaptain && !!currentDispatch ? currentDispatch.vesselId : undefined,
        startDate: startDate,
        endDate: endDate,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: isCaptain == null || (isCaptain ? !currentDispatch : fleet == null),
      }
    );

  useEffect(() => {
    if (!isFleetsLoading && fleets?.length === 1) {
      setFleet(fleets[0]);
    }
  }, [isFleetsLoading, fleets]);

  const createTable = (data: Evaluation[]): JSX.Element => (
    <TableContainer>
      <Table className="table" style={{ width: "100%" }}>
        <TableHead>
          <TableCell style={{ width: "25%" }}>Vessel</TableCell>
          <TableCell style={{ width: "25%" }}>Rating</TableCell>
          <TableCell style={{ width: "25%" }}>Mariner Name</TableCell>
          <TableCell style={{ width: "25%" }}>Date Completed</TableCell>
        </TableHead>
        <TableBody>
          {data.map((row) => (
            <TableRow key={`${row.id}`}>
              <TableCell>{row.vessel?.name}</TableCell>
              <TableCell className="rating left wb-wrap">
                <div className="rating-inner">
                  <Tooltip
                    title={
                      row.profile ? JobRatingLabel[row.rating! || row.profile.rating!] : ""
                    }
                    placement="right"
                  >
                    <span>{(row.rating || row.profile?.rating!).substring(1)}</span>
                  </Tooltip>
                </div>
              </TableCell>
              <TableCell>
                <NavLink
                  className="blue-link table-link"
                  to={`/evaluations/${row.id}`}
                >
                  {[
                    row.profile?.firstName,
                    row.profile?.middleName,
                    row.profile?.lastName,
                  ].join(" ")}
                </NavLink>
              </TableCell>
              <TableCell>
                {moment(row.archivedDate).format("MMM YYYY")}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <div className="flex-col wd-100">
        <div className="flex align-items-center mb-1 archives-header">
          <h1>PERFORMANCE EVALUATION ARCHIVES</h1>
        </div>
        <div>
          <label className="large-filter" style={{ width: "200px" }}>
            <span>Archived Date:</span>
          </label>
          <div className="flex g-1 align-items-center">
            <label htmlFor="archiveStartDate">
              <input
                type="date"
                id="archiveStartDate"
                value={startDate?.substring(0, 10) || ""}
                onChange={(event) =>
                  setStartDate(
                    !!event.target.value
                      ? new Date(event.target.value).toISOString()
                      : undefined
                  )
                }
              />
            </label>
            <ArrowRightAltOutlinedIcon />
            <label htmlFor="archiveEndDate">
              <input
                type="date"
                id="archiveEndDate"
                value={endDate?.substring(0, 10) || ""}
                onChange={(event) =>
                  setEndDate(
                    !!event.target.value
                      ? new Date(event.target.value).toISOString()
                      : undefined
                  )
                }
              />
            </label>
          </div>
        </div>
        {isCaptain ? (
          currentDispatch?.vessel?.name && (
            <label className="large-filter" style={{ width: "250px" }}>
              <br />
              <span>Vessel:</span>
              <Autocomplete
                className="fleet-dropdown"
                value={currentDispatch.vessel.name}
                options={[]}
                renderInput={(params) => <TextField {...params} />}
                getOptionLabel={(opt) => (opt ? opt : "")}
                disabled
              />
            </label>
          )
        ) : (
          !isFleetsLoading && (
            <label className="large-filter" style={{ width: "250px" }}>
              <br />
              <span>Fleet:</span>
              <Autocomplete
                // ARC shouldn't be available in fleet dropdown
                className="fleet-dropdown"
                options={
                  fleets
                    ? (fleets as Fleet[]).filter((fleet) => fleet.name !== "ARC")
                    : []
                }
                value={fleet}
                onChange={(_, value) => {
                  if (value != null) {
                    setFleet(value);
                  }
                }}
                renderInput={(params) => <TextField {...params} />}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                getOptionLabel={(opt) => (opt.name ? opt.name : "")}
              />
            </label>
          )
        )}
        <div>
          {!!evaluationArchives && !isEvaluationArchivesLoading != null && (
            <>
              <>
                <h2 style={{ marginTop: "2%" }}>Senior Officer Evaluations</h2>
                <div
                  style={{
                    height: "1%",
                    width: "100%",
                    backgroundColor: "#038f2d",
                  }}
                />
                {!!evaluationArchives.seniorOfficerEvaluations &&
                  evaluationArchives.seniorOfficerEvaluations.length ?
                  createTable(evaluationArchives.seniorOfficerEvaluations) :
                  "No Senior Officer Evaluations to Display"
                }
              </>
              <>
                <h2 style={{ marginTop: "2%" }}>Junior Officer Evaluations</h2>
                <div
                  style={{
                    height: "1%",
                    width: "100%",
                    backgroundColor: "#038f2d",
                  }}
                />
                {!!evaluationArchives.juniorOfficerEvaluations &&
                  evaluationArchives.juniorOfficerEvaluations.length ?
                  createTable(evaluationArchives.juniorOfficerEvaluations) :
                  "No Junior Officer Evaluations to Display"
                }
              </>
              <>
                <h2 style={{ marginTop: "2%" }}> Unlicensed Evaluations</h2>
                <div
                  style={{
                    height: "1%",
                    width: "100%",
                    backgroundColor: "#038f2d",
                  }}
                />
                {!!evaluationArchives.unlicensedEvaluations &&
                  evaluationArchives.unlicensedEvaluations.length ?
                  createTable(evaluationArchives.unlicensedEvaluations) :
                  "No Unlicensed Evaluations to Display"
                }
              </>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default EvaluationArchive;
